import XKDK_ABI from 'abis/XKDK.json'
import { ButtonPrimary } from 'components/Button'
import { LightCard } from 'components/Card'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import { RowBetween } from 'components/Row'
import { useApproveStep } from 'components/StepLine/common/ApproveStep'
import useStepWithNext from 'components/StepLine/context/hooks/useStepWithNext'
import StepLineModal from 'components/StepLine/StepLineModal'
import { Switch } from 'components/Switch'
import { KDK_ADDRESSES, KODIAK_REWARDS_ADDRESSES, XKDK_ADDRESSES } from 'constants/addresses'
import { useToken } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import { useWriteContract } from 'hooks/useTransaction'
import useWaitForTransactionReceipt from 'hooks/useWaitForTransactionReceipt'
import { useTokenBalance } from 'lib/hooks/useCurrencyBalance'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import { useMemo, useState } from 'react'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import type { Abi } from 'viem'

import { REFETCH_POLICY_FAST } from '../../config/RefetchPolicies'
import ConvertStep from './ConvertStep'
import StakePromptStep from './StakePromptStep'
import { TransactionInfoText } from './styleds'

const ConvertView = ({ onStake }: { onStake?: () => void }) => {
  const [inputKdk, setInputKdk] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [convertOption, setConvertOption] = useState<'convertOnly' | 'convertAndStake'>('convertAndStake')
  const stakeImmediately = convertOption === 'convertAndStake'
  const { chainId, account, wagmiPublicClient } = useConnection()
  const kdkAddress = chainId ? KDK_ADDRESSES[chainId] : undefined
  const xKdkAddress = chainId ? XKDK_ADDRESSES[chainId] : undefined
  const kdkToken = useToken(kdkAddress)
  const amount = useMemo(() => {
    return tryParseCurrencyAmount(inputKdk, kdkToken || undefined)
  }, [inputKdk, kdkToken])
  const balance = useTokenBalance(account, kdkToken || undefined, REFETCH_POLICY_FAST)
  const isInsufficientBalance = Boolean(amount && balance && amount.greaterThan(balance))
  const isActionDisabled = !amount || !amount.greaterThan('0') || !balance || isInsufficientBalance
  const actionLabel = isInsufficientBalance
    ? 'Insufficient KDK Amount'
    : stakeImmediately
    ? 'Convert & Stake'
    : 'Convert Only'

  const [attemptingTxn, setAttemptingTxn] = useState(false)
  const [hash, setHash] = useState('')
  const { writeContractAsync } = useWriteContract()
  const addTransaction = useTransactionAdder()
  const waitForTransactionReceipt = useWaitForTransactionReceipt()

  const onConvert = async () => {
    if (!xKdkAddress || !amount || !wagmiPublicClient || !account) return
    const quotient = amount.quotient.toString()

    setAttemptingTxn(true)
    try {
      let tx
      if (stakeImmediately && KODIAK_REWARDS_ADDRESSES[chainId]) {
        tx = {
          address: xKdkAddress,
          abi: XKDK_ABI as Abi,
          functionName: 'convertAndAllocate',
          args: [BigInt(quotient), KODIAK_REWARDS_ADDRESSES[chainId], '0x'],
          account,
        }
      } else {
        tx = {
          address: xKdkAddress,
          abi: XKDK_ABI as Abi,
          functionName: 'convert',
          args: [BigInt(quotient)],
          account,
        }
      }
      const gas = await wagmiPublicClient.estimateContractGas(tx)

      const txHash = await writeContractAsync({ ...tx, gas: calculateGasMargin(gas) })

      setHash(txHash)
      addTransaction(
        { hash: txHash },
        {
          type: TransactionType.STAKE_CONVERT,
          amount: `${amount?.toSignificant(6)} ${amount?.currency?.symbol}`,
          isStake: stakeImmediately,
        }
      )

      await waitForTransactionReceipt({
        hash: txHash,
      })
    } catch (error) {
      console.error('Error converting KDK:', error)
    } finally {
      setAttemptingTxn(false)
    }
  }

  const approveStep = useApproveStep(amount, xKdkAddress)
  const convertStep = useStepWithNext('convert-step', ConvertStep, {
    amount,
    attemptingTxn,
    pendingText: (
      <>
        {stakeImmediately ? 'Converting and Staking' : 'Converting'} {amount?.toSignificant(6)}{' '}
        {amount?.currency.symbol}
      </>
    ),
    hash,
    autoConfirm: true,
    transactionWaiting: true,
    onConvert,
    buttonLabel: stakeImmediately ? 'Convert & Stake' : 'Convert Only',
  })

  const stakePromptStep = useStepWithNext('stake-prompt-step', StakePromptStep, {
    onStake: () => {
      setIsModalOpen(false)
      onStake?.()
    },
    onClose: () => setIsModalOpen(false),
  })

  const handleConvert = () => {
    if (isActionDisabled) return
    setHash('')
    setAttemptingTxn(false)
    setIsModalOpen(true)
  }

  const handleFinish = () => {
    setIsModalOpen(false)
    setHash('')
    setAttemptingTxn(false)
  }
  return (
    <>
      <StepLineModal
        steps={stakeImmediately ? [approveStep, convertStep] : [approveStep, convertStep, stakePromptStep]}
        isOpen={isModalOpen}
        onFinish={handleFinish}
        onDismiss={handleFinish}
      />
      <CurrencyInputPanel
        // hideBalance={true}
        value={inputKdk}
        onUserInput={setInputKdk}
        onMax={() => setInputKdk(balance?.toExact() ?? '0')}
        showMaxButton={true}
        currency={kdkToken}
        label="kdk"
        // onCurrencySelect={handleSelectCurrencyA}
        id="convert-kdk"
      />
      <LightCard>
        <RowBetween>
          <TransactionInfoText>You will receive</TransactionInfoText>
          <TransactionInfoText>{inputKdk || '-'} xKDK</TransactionInfoText>
        </RowBetween>
      </LightCard>

      <div style={{ margin: '12px 0' }}>
        <Switch
          options={[
            { value: 'convertOnly', label: 'Convert Only' },
            { value: 'convertAndStake', label: 'Convert & Stake' },
          ]}
          activeValue={convertOption}
          setActiveValue={(value) => setConvertOption(value as 'convertOnly' | 'convertAndStake')}
        />
      </div>

      <ButtonPrimary disabled={isActionDisabled} onClick={handleConvert}>
        {actionLabel}
      </ButtonPrimary>
    </>
  )
}

export default ConvertView
